/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { Button, ButtonVariety, ModalAlert, RadioButtonGroup } from '@sonarsource/echoes-react';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AtlassianAccessibleResource } from '../../types';

interface OrganizationJiraPatchModalProps {
  isOpen: boolean;
  isPatchingBinding: boolean;
  onCancel: () => void;
  onConfirm: (resource: AtlassianAccessibleResource) => void;
  resources: AtlassianAccessibleResource[];
}

export function OrganizationJiraPatchModal(props: Readonly<OrganizationJiraPatchModalProps>) {
  const { isOpen, isPatchingBinding, onCancel, onConfirm, resources } = props;
  const { formatMessage } = useIntl();

  const [selectedResource, setSelectedResource] = useState(
    undefined as AtlassianAccessibleResource | undefined,
  );

  // initially set the first resource as selected
  useEffect(() => {
    setSelectedResource(resources[0]);
  }, [resources]);

  const changeResource = useCallback(
    (selectedCloudId: string) => {
      const selected = resources.find((r) => r.cloudId === selectedCloudId);
      if (selected) {
        setSelectedResource(selected);
      }
    },
    [resources],
  );

  return (
    <ModalAlert
      content={
        resources.length > 0 && (
          <RadioButtonGroup
            alignment="vertical"
            label=""
            onChange={changeResource}
            options={resources.map((x) => ({
              label: x.url.replace('https://', ''),
              value: x.cloudId,
            }))}
            value={selectedResource?.cloudId} // the ? is because the modal loads with the parent even if it not shown.
          />
        )
      }
      description={formatMessage({
        id: 'organization.jira.binding.modal.confirmation.description',
      })}
      isOpen={isOpen}
      onOpenChange={onCancel}
      primaryButton={
        <Button
          isDisabled={isPatchingBinding || selectedResource === undefined}
          isLoading={isPatchingBinding}
          onClick={() => {
            if (selectedResource !== undefined) {
              onConfirm(selectedResource);
            }
          }}
          variety={ButtonVariety.Primary}
        >
          <FormattedMessage id="confirm" />
        </Button>
      }
      secondaryButtonLabel={formatMessage({ id: 'cancel' })}
      title={formatMessage({ id: 'organization.jira.binding.modal.confirmation.title' })}
    />
  );
}
